/**
 * Notification bell for the console header.
 *
 * The admin console had no notifications at all: a KYC submission or a support
 * request only surfaced if someone thought to open the right list. Each item
 * carries the path it came from, so clicking it lands on the thing that needs
 * attention rather than a list to search.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  use_mark_all_read,
  use_mark_read,
  use_notifications,
  use_unread_count,
} from '@/features/notifications/hooks';

function ago(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days < 7 ? `${days}d ago` : new Date(iso).toLocaleDateString();
}

export function NotificationBell() {
  const [open, set_open] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: unread = 0 } = use_unread_count();
  // Only fetch the list once the panel is actually opened; the badge alone
  // doesn't need 30 rows on every page load.
  const { data: items, isLoading } = use_notifications(open);
  const mark_read = use_mark_read();
  const mark_all = use_mark_all_read();

  useEffect(() => {
    if (!open) return;
    function on_click(e: MouseEvent) {
      if (!wrapper.current?.contains(e.target as Node)) set_open(false);
    }
    function on_key(e: KeyboardEvent) {
      if (e.key === 'Escape') set_open(false);
    }
    document.addEventListener('mousedown', on_click);
    document.addEventListener('keydown', on_key);
    return () => {
      document.removeEventListener('mousedown', on_click);
      document.removeEventListener('keydown', on_key);
    };
  }, [open]);

  function activate(id: string, link_path: string | null, is_read: boolean) {
    if (!is_read) mark_read.mutate(id);
    set_open(false);
    if (link_path) navigate(link_path);
  }

  return (
    <div className="relative" ref={wrapper}>
      <Button
        variant="ghost"
        size="icon"
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        data-testid="notification-bell"
        onClick={() => set_open((v) => !v)}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            className="absolute right-1 top-1 min-w-[1.1rem] rounded-full bg-destructive px-1 text-[10px] font-bold leading-4 text-destructive-foreground"
            data-testid="notification-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute right-0 z-50 mt-2 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-border bg-card shadow-l2"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <span className="text-sm font-semibold">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                data-testid="notification-mark-all"
                onClick={() => mark_all.mutate()}
                disabled={mark_all.isPending}
              >
                <Check className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center p-6 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : !items || items.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground" data-testid="notification-empty">
                Nothing needs your attention.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => {
                  const is_read = n.read_at !== null;
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => activate(n.id, n.link_path, is_read)}
                        className={`w-full px-4 py-3 text-left transition-colors hover:bg-muted/60 ${is_read ? '' : 'bg-primary/5'}`}
                        data-testid={`notification-${n.id}`}
                      >
                        <div className="flex items-start gap-2">
                          {!is_read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                          <div className={is_read ? 'pl-4' : ''}>
                            <p className="text-sm font-semibold leading-tight">{n.title}</p>
                            <p className="mt-0.5 text-sm text-muted-foreground">{n.body}</p>
                            <p className="mt-1 text-xs text-muted-foreground">{ago(n.created_at)}</p>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
